import React, { useMemo, useRef, useState } from 'react';
import { RouterPlaygroundEmbed } from '../../RouterPlaygroundPage';
import { RouterArchitectureFigure } from './RouterArchitectureFigure';
import { BlogFigure } from '../BlogFigure';
import { getBlogEmbedMetadata } from './metadata';
import type { BlogEmbedId } from '../types';
import { KetteiFigure, isKetteiEmbed } from './kettei/KetteiFigure';

type XY = [number, number];

function spline(points: XY[], tension = 0.72) {
  if (points.length < 2) return '';
  let path = `M ${points[0][0]} ${points[0][1]}`;
  for (let index = 0; index < points.length - 1; index += 1) {
    const p0 = points[index - 1] || points[index];
    const p1 = points[index];
    const p2 = points[index + 1];
    const p3 = points[index + 2] || p2;
    const c1x = p1[0] + ((p2[0] - p0[0]) * tension) / 6;
    const c1y = p1[1] + ((p2[1] - p0[1]) * tension) / 6;
    const c2x = p2[0] - ((p3[0] - p1[0]) * tension) / 6;
    const c2y = p2[1] - ((p3[1] - p1[1]) * tension) / 6;
    path += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2[0]} ${p2[1]}`;
  }
  return path;
}

function starPoints(cx: number, cy: number, outerRadius: number, innerRadius: number) {
  return Array.from({ length: 10 }, (_, index) => {
    const angle = -Math.PI / 2 + (index * Math.PI) / 5;
    const radius = index % 2 === 0 ? outerRadius : innerRadius;
    return `${cx + Math.cos(angle) * radius},${cy + Math.sin(angle) * radius}`;
  }).join(' ');
}

function RouterEmbedFigure({
  name,
  children,
  frameClassName,
}: {
  name: BlogEmbedId;
  children: React.ReactNode;
  frameClassName?: string;
}) {
  const metadata = getBlogEmbedMetadata(name);
  return (
    <BlogFigure
      id={metadata.anchorId}
      figureNumber={metadata.figureNumber}
      caption={metadata.caption}
      frameClassName={frameClassName}
    >
      {children}
    </BlogFigure>
  );
}

function RoutingQuestionContrastEmbed() {
  return (
    <dl className="border-y border-border">
      <div className="grid gap-2 py-5 sm:grid-cols-[112px_minmax(0,1fr)] sm:items-start sm:gap-7">
        <dt className="pt-1 font-mono text-[11px] uppercase tracking-[0.14em] text-foreground-muted">
          Embedding
        </dt>
        <dd className="font-body text-[19px] font-medium leading-[1.45] text-foreground sm:text-[21px]">
          What is this text about?
        </dd>
      </div>
      <div className="grid gap-2 border-t border-border py-5 sm:grid-cols-[112px_minmax(0,1fr)] sm:items-start sm:gap-7">
        <dt className="pt-1 font-mono text-[11px] uppercase tracking-[0.14em] text-foreground-muted">
          Routing
        </dt>
        <dd className="font-body text-[19px] font-medium leading-[1.45] text-foreground sm:text-[21px]">
          What work will this request require?<br className="hidden sm:block" /> Which model in the pool is best equipped to do it?
        </dd>
      </div>
    </dl>
  );
}

const FRONTIER_FAMILY: XY[] = [
  [0.099, 69.73], [0.108, 70.36], [0.145, 71.33], [0.159, 71.86],
  [0.195, 72.27], [0.218, 72.63], [0.285, 72.80], [0.336, 73.16],
  [0.394, 73.49], [0.397, 74.00], [0.453, 74.19], [0.610, 74.25],
  [0.652, 74.63], [0.665, 74.80], [0.679, 75.10], [0.791, 75.42],
];

const FRONTIER_ORACLE: XY[] = [
  [0.085, 79.7], [0.120, 81.2], [0.180, 82.4], [0.280, 83.2],
  [0.420, 83.55], [0.600, 83.62],
];

const FRONTIER_SQWISH_SWEEP: XY[] = [
  [0.70, 77.62], [0.61, 77.57], [0.54, 77.49], [0.49, 77.53],
  [0.42, 77.44], [0.36, 77.36], [0.33, 77.25], [0.30, 77.12],
  [0.27, 77.15], [0.24, 76.92], [0.22, 76.76], [0.20, 76.63],
  [0.18, 76.40], [0.16, 76.08], [0.145, 75.82], [0.13, 75.46],
  [0.115, 75.02], [0.10, 74.42],
];

type FrontierModel = {
  name: string;
  cost: number;
  accuracy: number;
  pareto: boolean;
  labelDx?: number;
  labelDy?: number;
};

type FrontierRouter = {
  name: string;
  label: string;
  rank: number;
  arena: number;
  cost: number;
  accuracy: number;
  robustness: number;
  dx: number;
  dy: number;
  kind?: 'sqwish';
};

type FrontierPoint = {
  type: 'router' | 'model';
  name: string;
  cost: number;
  accuracy: number;
  rank?: number;
  arena?: number;
  pareto?: boolean;
};

const FRONTIER_MODELS: FrontierModel[] = [
  { name: 'Qwen3 235B', cost: 0.099, accuracy: 69.73, pareto: true },
  { name: 'Qwen3 Coder', cost: 0.304, accuracy: 69.12, pareto: false },
  { name: 'DeepSeek V4', cost: 0.429, accuracy: 71.85, pareto: true },
  { name: 'Qwen3 Next', cost: 0.758, accuracy: 69.06, pareto: false },
  { name: 'Gemini 3.1 Flash-Lite', cost: 1.05, accuracy: 74.84, pareto: true, labelDx: -10, labelDy: 32 },
];

const FRONTIER_ROUTERS: FrontierRouter[] = [
  { name: 'R2-Router', label: 'R2-Router', rank: 9, arena: 71.60, cost: 0.06, accuracy: 71.23, robustness: 45.71, dx: 10, dy: 19 },
  { name: 'AgentForge Router', label: 'AgentForge', rank: 3, arena: 74.13, cost: 0.13, accuracy: 74.72, robustness: 40.48, dx: 14, dy: 20 },
  { name: 'Sqwish Router', label: 'Sqwish', rank: 2, arena: 75.27, cost: 0.18, accuracy: 76.40, robustness: 100.00, dx: 15, dy: -20, kind: 'sqwish' },
  { name: 'LLM Router', label: 'LLM Router', rank: 10, arena: 71.26, cost: 0.20, accuracy: 72.05, robustness: 30.00, dx: 12, dy: 18 },
  { name: 'Cross-Router', label: 'Cross-Router', rank: 4, arena: 73.71, cost: 0.26, accuracy: 75.13, robustness: 59.05, dx: 12, dy: 21 },
  { name: 'vLLM-SR', label: 'vLLM-SR', rank: 1, arena: 75.30, cost: 0.30, accuracy: 77.18, robustness: 67.62, dx: 13, dy: -18 },
  { name: 'Nadir Router', label: 'Nadir', rank: 6, arena: 72.29, cost: 0.68, accuracy: 75.01, robustness: 25.48, dx: 12, dy: -18 },
  { name: 'Azure-Model-Router', label: 'Azure', rank: 11, arena: 70.42, cost: 0.73, accuracy: 72.94, robustness: 71.43, dx: 14, dy: 28 },
  { name: 'Weave Router', label: 'Weave', rank: 5, arena: 72.82, cost: 0.94, accuracy: 76.32, robustness: 100.00, dx: -14, dy: -18 },
  { name: 'OrcaRouter-Adaptive', label: 'OrcaRouter', rank: 7, arena: 72.08, cost: 1.0, accuracy: 75.54, robustness: 22.62, dx: -14, dy: 19 },
  { name: 'NotDiamond', label: 'NotDiamond', rank: 19, arena: 57.29, cost: 4.1, accuracy: 60.83, robustness: 55.91, dx: 12, dy: -15 },
];

function RouterFrontierFigure() {
  const [activePoint, setActivePoint] = useState<FrontierPoint | null>(null);
  const left = 90;
  const right = 1058;
  const top = 74;
  const bottom = 548;
  const x0 = 0.035;
  const x1 = 5.2;
  const y0 = 58;
  const y1 = 85;
  const x = (value: number) => right - ((Math.log10(value) - Math.log10(x0)) / (Math.log10(x1) - Math.log10(x0))) * (right - left);
  const y = (value: number) => bottom - ((value - y0) / (y1 - y0)) * (bottom - top);
  const familyPath = spline(FRONTIER_FAMILY.map(([cost, accuracy]) => [x(cost), y(accuracy)]));
  const oraclePath = spline(FRONTIER_ORACLE.map(([cost, accuracy]) => [x(cost), y(accuracy)]));
  const sqwishSweepPath = spline(FRONTIER_SQWISH_SWEEP.map(([cost, accuracy]) => [x(cost), y(accuracy)]), 0.72);
  const fixedPath = FRONTIER_MODELS.filter(model => model.pareto)
    .sort((a, b) => a.cost - b.cost)
    .map((model, index) => `${index ? 'L' : 'M'} ${x(model.cost)} ${y(model.accuracy)}`)
    .join(' ');
  const activeX = activePoint ? x(activePoint.cost) : 0;
  const activeY = activePoint ? y(activePoint.accuracy) : 0;
  const tooltipWidth = 318;
  const tooltipHeight = 84;
  const tooltipX = activePoint
    ? Math.max(left + 8, Math.min(activeX + (activeX > right - tooltipWidth - 28 ? -tooltipWidth - 18 : 18), right - tooltipWidth - 8))
    : 0;
  const tooltipY = activePoint
    ? Math.max(top + 8, Math.min(activeY - tooltipHeight / 2, bottom - tooltipHeight - 8))
    : 0;

  return (
    <RouterEmbedFigure name="router-frontier" frameClassName="px-2 py-3 sm:px-4 sm:py-4">
        <svg viewBox="0 0 1140 640" className="block h-auto w-full max-w-full" role="img" aria-labelledby="router-frontier-title router-frontier-desc">
          <title id="router-frontier-title">Accuracy and cost on RouterArena, 14 July 2026</title>
          <desc id="router-frontier-desc">A snapshot from 14 July 2026. Diamonds are fixed models and circles are routers. The solid curve is the family lookup, the dotted fixed-model envelope joins the strongest individual models at each cost, and the oracle curve is the best result available with per-request hindsight. The Sqwish submission is marked by a star.</desc>
          {[60, 65, 70, 75, 80, 85].map(tick => (
            <g key={tick}>
              <line x1={left} x2={right} y1={y(tick)} y2={y(tick)} className="stroke-border" strokeWidth="1" />
              <text x={left - 16} y={y(tick) + 6} textAnchor="end" className="fill-foreground-secondary font-mono text-[26px] sm:text-[20px]">{tick}%</text>
            </g>
          ))}
          {[0.05, 0.1, 0.2, 0.5, 1, 2, 5].map(tick => (
            <g key={tick}>
              <line x1={x(tick)} x2={x(tick)} y1={top} y2={bottom} className="stroke-border opacity-50" strokeWidth="1" strokeDasharray="2 7" />
              <text x={x(tick)} y={bottom + 32} textAnchor="middle" className="fill-foreground-secondary font-mono text-[26px] sm:text-[20px]">${tick}</text>
            </g>
          ))}
          <text
            x="28"
            y={top + 20}
            textAnchor="end"
            dominantBaseline="middle"
            transform={`rotate(-90 28 ${top + 20})`}
            className="hidden fill-foreground-secondary font-mono text-[18px] uppercase tracking-[0.1em] sm:block"
          >
            Accuracy · smarter
          </text>
          <text x="28" y={top - 10} textAnchor="middle" className="hidden fill-foreground-secondary font-mono text-[21px] sm:block">↑</text>
          <text x={left} y={top - 4} className="fill-foreground-secondary font-mono text-[24px] uppercase tracking-[0.06em] sm:hidden">Accuracy · smarter ↑</text>
          <text x={right} y={bottom + 64} textAnchor="end" className="hidden fill-foreground-secondary font-mono text-[18px] uppercase tracking-[0.08em] sm:block">Cost / 1k (log) · cheaper →</text>
          <text x={right} y={bottom + 64} textAnchor="end" className="fill-foreground-secondary font-mono text-[24px] uppercase tracking-[0.05em] sm:hidden">Cost / 1k (log) · cheaper →</text>

          <path d={oraclePath} className="fill-none stroke-brand-mint" strokeWidth="3.2" strokeLinecap="round" />
          <path d={familyPath} className="fill-none stroke-foreground" strokeWidth="2.2" strokeLinecap="round" />
          <path d={fixedPath} className="fill-none stroke-foreground-muted" strokeWidth="1.4" strokeDasharray="1 6" strokeLinecap="round" />
          <path d={sqwishSweepPath} className="fill-none stroke-brand-amber" strokeWidth="1.8" strokeDasharray="1 7" strokeLinecap="round" opacity="0.58" />

          <g className="hidden font-mono text-[18px] uppercase tracking-[0.07em] sm:block">
            <line x1="90" x2="124" y1="24" y2="24" className="stroke-brand-mint" strokeWidth="3.2" />
            <text x="134" y="30" className="fill-foreground-secondary">Oracle</text>
            <line x1="228" x2="262" y1="24" y2="24" className="stroke-foreground" strokeWidth="2.2" />
            <text x="272" y="30" className="fill-foreground-secondary">Family lookup</text>
            <line x1="430" x2="464" y1="24" y2="24" className="stroke-foreground-muted" strokeWidth="1.4" strokeDasharray="1 6" strokeLinecap="round" />
            <text x="474" y="30" className="fill-foreground-secondary">Best fixed model</text>
            <line x1="710" x2="744" y1="24" y2="24" className="stroke-brand-amber" strokeWidth="1.8" strokeDasharray="1 7" strokeLinecap="round" />
            <text x="754" y="30" className="fill-foreground-secondary">Sqwish sweep</text>
          </g>
          <g className="font-mono text-[24px] uppercase tracking-[0.04em] sm:hidden">
            <line x1="90" x2="120" y1="24" y2="24" className="stroke-brand-mint" strokeWidth="3" />
            <text x="130" y="31" className="fill-foreground-secondary">Oracle</text>
            <line x1="270" x2="300" y1="24" y2="24" className="stroke-foreground" strokeWidth="2.2" />
            <text x="310" y="31" className="fill-foreground-secondary">Family</text>
            <line x1="450" x2="480" y1="24" y2="24" className="stroke-foreground-muted" strokeWidth="1.5" strokeDasharray="1 6" strokeLinecap="round" />
            <text x="490" y="31" className="fill-foreground-secondary">Best fixed</text>
            <line x1="720" x2="750" y1="24" y2="24" className="stroke-brand-amber" strokeWidth="2" strokeDasharray="1 7" strokeLinecap="round" />
            <text x="760" y="31" className="fill-foreground-secondary">Sqwish sweep</text>
          </g>
          <text x="1058" y="58" textAnchor="end" className="fill-foreground-muted font-mono text-[20px] uppercase tracking-[0.08em] sm:text-[15px]">
            Snapshot · 14 Jul 2026
          </text>

          <g className="hidden font-mono text-[16px] uppercase tracking-[0.06em] sm:block" transform="translate(106 94)">
            <rect width="214" height="38" rx="9" className="fill-background stroke-border" strokeWidth="1" opacity="0.92" />
            <path d="M 18 13 L 24 19 L 18 25 L 12 19 Z" className="fill-background stroke-foreground-muted" strokeWidth="1.3" />
            <text x="34" y="24" className="fill-foreground-secondary">Models</text>
            <circle cx="126" cy="19" r="5" className="fill-background stroke-foreground-secondary" strokeWidth="1.5" />
            <text x="140" y="24" className="fill-foreground-secondary">Routers</text>
          </g>
          <g className="font-mono text-[21px] uppercase tracking-[0.04em] sm:hidden" transform="translate(90 94)">
            <rect width="330" height="48" rx="11" className="fill-background stroke-border" strokeWidth="1.2" opacity="0.94" />
            <path d="M 24 15 L 33 24 L 24 33 L 15 24 Z" className="fill-background stroke-foreground-muted" strokeWidth="1.6" />
            <text x="48" y="31" className="fill-foreground-secondary">Models</text>
            <circle cx="194" cy="24" r="7" className="fill-background stroke-foreground-secondary" strokeWidth="1.8" />
            <text x="215" y="31" className="fill-foreground-secondary">Routers</text>
          </g>

          {FRONTIER_MODELS.map(model => {
            const isActive = activePoint?.type === 'model' && activePoint.name === model.name;
            const point: FrontierPoint = { type: 'model', ...model };
            return (
            <g
              key={model.name}
              opacity={model.pareto ? 0.9 : 0.52}
              tabIndex={0}
              aria-label={`${model.name}, fixed model, ${model.accuracy.toFixed(2)} percent accuracy, $${model.cost.toFixed(2)} per thousand requests`}
              onPointerEnter={event => { if (event.pointerType !== 'touch') setActivePoint(point); }}
              onPointerLeave={event => { if (event.pointerType !== 'touch') setActivePoint(current => current?.name === model.name ? null : current); }}
              onFocus={() => setActivePoint(point)}
              onBlur={() => setActivePoint(current => current?.name === model.name ? null : current)}
              onPointerDown={event => { if (event.pointerType === 'touch') setActivePoint(current => current?.name === model.name ? null : point); }}
              style={{ cursor: 'help', outline: 'none' }}
            >
              <circle cx={x(model.cost)} cy={y(model.accuracy)} r="12" fill="transparent" />
              {isActive && <circle cx={x(model.cost)} cy={y(model.accuracy)} r="9" className="fill-none stroke-foreground-secondary" strokeWidth="1" opacity="0.45" />}
              <rect x={x(model.cost) - 4.5} y={y(model.accuracy) - 4.5} width="9" height="9" transform={`rotate(45 ${x(model.cost)} ${y(model.accuracy)})`} className="fill-background stroke-foreground-muted" strokeWidth="1.35" />
              {model.pareto && (
                <text
                  x={x(model.cost) + (model.labelDx ?? 9)}
                  y={y(model.accuracy) + (model.labelDy ?? 19)}
                  textAnchor={(model.labelDx ?? 9) < 0 ? 'end' : 'start'}
                  className="hidden fill-foreground-secondary font-mono text-[17px] uppercase tracking-[0.03em] min-[480px]:block"
                >
                  {model.name}
                </text>
              )}
            </g>
          );})}

          {FRONTIER_ROUTERS.map(router => {
            const highlighted = router.kind === 'sqwish';
            const isActive = activePoint?.type === 'router' && activePoint.name === router.name;
            const point: FrontierPoint = {
              type: 'router', name: router.name, cost: router.cost, accuracy: router.accuracy,
              rank: router.rank, arena: router.arena,
            };
            const textAnchor = (router.dx || 0) < 0 ? 'end' : 'start';
            return (
              <g
                key={router.name}
                tabIndex={0}
                aria-label={`${router.name}, router, Arena score ${router.arena.toFixed(1)}, ${router.accuracy.toFixed(2)} percent accuracy, $${router.cost.toFixed(2)} per thousand requests`}
                onPointerEnter={event => { if (event.pointerType !== 'touch') setActivePoint(point); }}
                onPointerLeave={event => { if (event.pointerType !== 'touch') setActivePoint(current => current?.name === router.name ? null : current); }}
                onFocus={() => setActivePoint(point)}
                onBlur={() => setActivePoint(current => current?.name === router.name ? null : current)}
                onPointerDown={event => { if (event.pointerType === 'touch') setActivePoint(current => current?.name === router.name ? null : point); }}
                style={{ cursor: 'help', outline: 'none' }}
              >
                <circle cx={x(router.cost)} cy={y(router.accuracy)} r="13" fill="transparent" />
                {isActive && <circle cx={x(router.cost)} cy={y(router.accuracy)} r="10" className={highlighted ? 'fill-none stroke-brand-amber' : 'fill-none stroke-foreground-secondary'} strokeWidth="1.1" opacity="0.48" />}
                {router.kind === 'sqwish' ? (
                  <polygon
                    points={starPoints(x(router.cost), y(router.accuracy), 10.5, 4.5)}
                    className="fill-brand-amber stroke-background"
                    strokeWidth="2.1"
                    strokeLinejoin="round"
                  />
                ) : (
                  <circle
                    cx={x(router.cost)}
                    cy={y(router.accuracy)}
                    r="4.8"
                    className="fill-background stroke-foreground-secondary"
                    strokeWidth="1.55"
                  />
                )}
                {router.dx !== undefined && (
                  <>
                    <line
                      x1={x(router.cost)}
                      y1={y(router.accuracy)}
                      x2={x(router.cost) + router.dx * 0.62}
                      y2={y(router.accuracy) + router.dy * 0.62}
                      className="stroke-border"
                      strokeWidth="1"
                    />
                    <text
                      x={x(router.cost) + router.dx}
                      y={y(router.accuracy) + router.dy}
                      textAnchor={textAnchor}
                      className={router.kind === 'sqwish' ? 'fill-brand-amber font-mono text-[26px] font-semibold uppercase sm:text-[20px]' : 'hidden fill-foreground-secondary font-mono text-[17px] uppercase min-[480px]:block'}
                    >
                      {router.label}
                    </text>
                  </>
                )}
              </g>
            );
          })}

          {activePoint && (
            <g pointerEvents="none">
              <line
                x1={activeX}
                y1={activeY}
                x2={tooltipX > activeX ? tooltipX : tooltipX + tooltipWidth}
                y2={Math.max(tooltipY + 14, Math.min(activeY, tooltipY + tooltipHeight - 14))}
                className="stroke-foreground-muted"
                strokeWidth="1"
                opacity="0.65"
              />
              <rect x={tooltipX} y={tooltipY} width={tooltipWidth} height={tooltipHeight} rx="9" className="fill-background stroke-border" strokeWidth="1.2" />
              <line x1={tooltipX + 1} x2={tooltipX + tooltipWidth - 1} y1={tooltipY + 1.5} y2={tooltipY + 1.5} className={activePoint.type === 'router' && activePoint.name === 'Sqwish Router' ? 'stroke-brand-amber' : activePoint.type === 'router' ? 'stroke-foreground-secondary' : 'stroke-foreground-muted'} strokeWidth="2" />
              <text x={tooltipX + 14} y={tooltipY + 23} className="fill-foreground font-mono text-[20px] font-semibold uppercase sm:text-[16px]">{activePoint.name}</text>
              {activePoint.type === 'router' ? (
                <>
                  <g className="hidden sm:block">
                    <text x={tooltipX + 14} y={tooltipY + 45} className="fill-foreground-secondary font-mono text-[14px] uppercase">
                      RouterArena score {activePoint.arena?.toFixed(1)}
                    </text>
                    <text x={tooltipX + 14} y={tooltipY + 67} className="fill-foreground-secondary font-mono text-[14px] uppercase">{activePoint.accuracy.toFixed(2)}% accuracy / ${activePoint.cost.toFixed(2)} per 1k</text>
                  </g>
                  <g className="sm:hidden">
                    <text x={tooltipX + 14} y={tooltipY + 45} className="fill-foreground-secondary font-mono text-[19px] uppercase">
                      Arena score {activePoint.arena?.toFixed(1)}
                    </text>
                    <text x={tooltipX + 14} y={tooltipY + 67} className="fill-foreground-secondary font-mono text-[19px] uppercase">{activePoint.accuracy.toFixed(2)}% / ${activePoint.cost.toFixed(2)}/1k</text>
                  </g>
                </>
              ) : (
                <>
                  <g className="hidden sm:block">
                    <text x={tooltipX + 14} y={tooltipY + 45} className="fill-foreground-secondary font-mono text-[14px] uppercase">Fixed model{activePoint.pareto ? ' / Pareto frontier' : ''}</text>
                    <text x={tooltipX + 14} y={tooltipY + 67} className="fill-foreground-secondary font-mono text-[14px] uppercase">{activePoint.accuracy.toFixed(2)}% accuracy / ${activePoint.cost.toFixed(2)} per 1k</text>
                  </g>
                  <g className="sm:hidden">
                    <text x={tooltipX + 14} y={tooltipY + 45} className="fill-foreground-secondary font-mono text-[19px] uppercase">Fixed model{activePoint.pareto ? ' / frontier' : ''}</text>
                    <text x={tooltipX + 14} y={tooltipY + 67} className="fill-foreground-secondary font-mono text-[19px] uppercase">{activePoint.accuracy.toFixed(2)}% / ${activePoint.cost.toFixed(2)}/1k</text>
                  </g>
                </>
              )}
            </g>
          )}
        </svg>
    </RouterEmbedFigure>
  );
}

function WeatherMapFigure() {
  const sampledLine = (
    transform: (u: number, v: number) => XY,
    fixed: number,
    vertical: boolean,
    samples: number,
  ) => Array.from({ length: samples + 1 }, (_, index) => {
    const value = -1 + (2 * index) / samples;
    return vertical ? transform(fixed, value) : transform(value, fixed);
  });

  const pathFrom = (
    transform: (u: number, v: number) => XY,
    fixed: number,
    vertical: boolean,
    samples = 32,
  ) => spline(sampledLine(transform, fixed, vertical, samples), 0.92);

  const sourceHeight = (u: number, v: number) => (
    22 * Math.sin(1.15 * Math.PI * u) * Math.cos(0.8 * Math.PI * v)
      + 10 * Math.sin(0.9 * Math.PI * v)
      + 8 * u * v
  );

  const targetHeight = (u: number, v: number) => {
    const q = v - 0.1 * u;
    const bulge = Math.exp(-0.82 * (u * u + v * v));
    return 132 * Math.tanh(10 * q) * bulge
      + 26 * Math.sin(1.35 * Math.PI * u) * Math.cos(0.8 * Math.PI * v)
      + 10 * u * v;
  };

  const projectSurface = (
    centerX: number,
    centerY: number,
    u: number,
    v: number,
    height: number,
    warp = 0,
  ): XY => [
    centerX + 126 * u + 34 * v + 0.10 * height + warp * Math.sin(Math.PI * v),
    centerY + 80 * v - 12 * u + 0.72 * height,
  ];

  const source = (u: number, v: number): XY => projectSurface(230, 320, u, v, sourceHeight(u, v));
  const target = (u: number, v: number): XY => projectSurface(860, 320, u, v, targetHeight(u, v), 10);

  const surfaceLight = (heightAt: (u: number, v: number) => number, u: number, v: number) => {
    const epsilon = 0.015;
    const dzdu = (heightAt(u + epsilon, v) - heightAt(u - epsilon, v)) / (2 * epsilon * 220);
    const dzdv = (heightAt(u, v + epsilon) - heightAt(u, v - epsilon)) / (2 * epsilon * 220);
    const normal: [number, number, number] = [-dzdu, -dzdv, 1];
    const length = Math.hypot(...normal);
    const lambert = (normal[0] * -0.34 + normal[1] * -0.42 + normal[2] * 0.84) / length;
    return Math.max(0, Math.min(1, 0.30 + 0.70 * lambert));
  };

  const surfaceLevels = [-1, -0.75, -0.5, -0.25, 0, 0.25, 0.5, 0.75, 1];
  const makeSurfaceCells = (
    transform: (u: number, v: number) => XY,
    heightAt: (u: number, v: number) => number,
  ) => surfaceLevels.slice(0, -1).flatMap((u0, uIndex) => (
    surfaceLevels.slice(0, -1).map((v0, vIndex) => {
      const u1 = surfaceLevels[uIndex + 1];
      const v1 = surfaceLevels[vIndex + 1];
      const u = (u0 + u1) / 2;
      const v = (v0 + v1) / 2;
      return {
        key: `${uIndex}-${vIndex}`,
        uv: [u0, v0, u1, v1] as const,
        points: [transform(u0, v0), transform(u1, v0), transform(u1, v1), transform(u0, v1)],
        depth: heightAt(u, v),
        light: surfaceLight(heightAt, u, v),
      };
    })
  )).sort((a, b) => a.depth - b.depth);

  const surfaceBoundary = (transform: (u: number, v: number) => XY) => {
    const edgeSamples = 24;
    const points: XY[] = [];
    for (let index = 0; index <= edgeSamples; index += 1) points.push(transform(-1 + (2 * index) / edgeSamples, -1));
    for (let index = 1; index <= edgeSamples; index += 1) points.push(transform(1, -1 + (2 * index) / edgeSamples));
    for (let index = 1; index <= edgeSamples; index += 1) points.push(transform(1 - (2 * index) / edgeSamples, 1));
    for (let index = 1; index <= edgeSamples; index += 1) points.push(transform(-1, 1 - (2 * index) / edgeSamples));
    points.push(points[0]);
    return `${spline(points, 0.90)} Z`;
  };

  const sourceSurfaceCells = makeSurfaceCells(source, sourceHeight);
  const targetSurfaceCells = makeSurfaceCells(target, targetHeight);
  const sourceSurfaceBoundary = surfaceBoundary(source);
  const targetSurfaceBoundary = surfaceBoundary(target);

  const levels = [-1, -0.75, -0.5, -0.3, -0.16, 0, 0.16, 0.3, 0.5, 0.75, 1];
  const dots: XY[] = [
    [-0.78, -0.66], [-0.58, -0.38], [-0.42, -0.72], [-0.31, -0.27], [-0.18, -0.58], [0.02, -0.34], [0.23, -0.69], [0.47, -0.32],
    [-0.68, -0.06], [-0.49, 0.18], [-0.30, -0.02], [-0.20, 0.37], [0.05, 0.10], [0.19, 0.42], [0.39, 0.05], [0.63, 0.31],
    [-0.73, 0.52], [-0.54, 0.73], [-0.31, 0.56], [-0.12, 0.78], [0.12, 0.57], [0.31, 0.76], [0.52, 0.49], [0.76, 0.68],
  ];
  const threadSeeds = dots.filter((_, index) => index % 4 !== 0);
  const highlighted = [
    { source: [-0.022, -0.022] as XY, target: [-0.04, -0.04] as XY, kind: 'pink' as const, label: 'HIGH-SCHOOL MATH' },
    { source: [0.022, 0.022] as XY, target: [0.04, 0.04] as XY, kind: 'amber' as const, label: 'UNSOLVED FOR 350 YEARS' },
  ];

  const mobileSource = (u: number, v: number): XY => {
    const [px, py] = source(u, v);
    return [195 + (px - 230) * 0.82, 270 + (py - 320) * 0.52];
  };
  const mobileTarget = (u: number, v: number): XY => {
    const [px, py] = target(u, v);
    return [195 + (px - 860) * 0.70, 665 + (py - 320) * 0.60];
  };
  const mapSurfaceCells = (
    cells: typeof sourceSurfaceCells,
    transform: (u: number, v: number) => XY,
  ) => cells.map(cell => {
    const [u0, v0, u1, v1] = cell.uv;
    return {
      ...cell,
      points: [transform(u0, v0), transform(u1, v0), transform(u1, v1), transform(u0, v1)],
    };
  });
  const mobileSourceSurfaceCells = mapSurfaceCells(sourceSurfaceCells, mobileSource);
  const mobileTargetSurfaceCells = mapSurfaceCells(targetSurfaceCells, mobileTarget);
  const mobileSourceSurfaceBoundary = surfaceBoundary(mobileSource);
  const mobileTargetSurfaceBoundary = surfaceBoundary(mobileTarget);

  return (
    <RouterEmbedFigure name="weather-map">
        <svg viewBox="0 0 1140 650" className="hidden h-auto w-full max-w-full md:block" role="img" aria-labelledby="weather-title weather-desc">
          <title id="weather-title">Two nearby prompts separate in model routing space</title>
          <desc id="weather-desc">Two nearly identical equations are close in text embedding space but far apart in difficulty. The faint paths suggest how sharply model rollouts can diverge after a small change in the prompt.</desc>
          <defs>
            <linearGradient id="weather-flow-fade" x1="0" x2="1">
              <stop offset="0" stopColor="hsl(var(--foreground-muted))" stopOpacity="0.22" />
              <stop offset="0.46" stopColor="hsl(var(--foreground-secondary))" stopOpacity="0.40" />
              <stop offset="1" stopColor="hsl(var(--foreground-muted))" stopOpacity="0.18" />
            </linearGradient>
          </defs>

          <g className="fill-foreground-secondary font-mono text-[18px] uppercase tracking-[0.12em]">
            <text x="48" y="48" paintOrder="stroke" stroke="hsl(var(--background))" strokeWidth="5">TEXT EMBEDDING SPACE</text>
            <text x="1092" y="48" textAnchor="end" paintOrder="stroke" stroke="hsl(var(--background))" strokeWidth="5">MODEL ROUTING SPACE</text>
          </g>

          <g aria-hidden="true">
            <path d={sourceSurfaceBoundary} fill="hsl(var(--brand-mint))" opacity="0.035" />
            <path d={targetSurfaceBoundary} fill="hsl(var(--brand-mint))" opacity="0.035" />
            {sourceSurfaceCells.map(cell => (
              <polygon
                key={`source-surface-${cell.key}`}
                points={cell.points.map(([px, py]) => `${px.toFixed(1)},${py.toFixed(1)}`).join(' ')}
                fill="hsl(var(--brand-mint))"
                opacity={0.020 + cell.light * 0.090}
              />
            ))}
            {targetSurfaceCells.map(cell => (
              <polygon
                key={`target-surface-${cell.key}`}
                points={cell.points.map(([px, py]) => `${px.toFixed(1)},${py.toFixed(1)}`).join(' ')}
                fill="hsl(var(--brand-mint))"
                opacity={0.020 + cell.light * 0.090}
              />
            ))}
          </g>

          {threadSeeds.map(([u, v], index) => {
            const [sourceX, sourceY] = source(u, v);
            const [targetX, targetY] = target(u, v);
            const phase = Math.sin((u * 13 + v * 17) * Math.PI);
            const bend = Math.cos((u * 19 - v * 11) * Math.PI);
            const path = spline([
              [sourceX, sourceY],
              [350 + 14 * phase, sourceY + 34 * bend],
              [445 + 24 * bend, 278 + 104 * phase],
              [565 + 14 * phase, 325 + 92 * bend],
              [665 - 18 * bend, 292 - 88 * phase],
              [758 + 20 * phase, targetY + 38 * bend],
              [targetX, targetY],
            ], 0.92);
            return <path key={`flow-${index}`} d={path} fill="none" stroke="url(#weather-flow-fade)" strokeWidth="1.1" strokeLinecap="round" opacity={0.50 + (index % 4) * 0.055} />;
          })}

          <g className="fill-none stroke-brand-mint" strokeWidth="1.05" strokeLinecap="round">
            {levels.map((level, index) => <path key={`source-v-${level}`} d={pathFrom(source, level, true, 36)} opacity={index === 0 || index === levels.length - 1 ? 0.72 : 0.43} />)}
            {levels.map((level, index) => <path key={`source-h-${level}`} d={pathFrom(source, level, false, 36)} opacity={index === 0 || index === levels.length - 1 ? 0.72 : 0.43} />)}
          </g>
          <g className="fill-none stroke-brand-mint" strokeWidth="1.08" strokeLinecap="round">
            {levels.map((level, index) => <path key={`target-v-${level}`} d={pathFrom(target, level, true, 48)} opacity={index === 0 || index === levels.length - 1 ? 0.72 : 0.43} />)}
            {levels.map((level, index) => <path key={`target-h-${level}`} d={pathFrom(target, level, false, 48)} opacity={index === 0 || index === levels.length - 1 ? 0.72 : 0.43} />)}
          </g>

          <g className="fill-brand-mint" opacity="0.84">
            {dots.map(([u, v], index) => { const [cx, cy] = source(u, v); return <circle key={`source-dot-${index}`} cx={cx} cy={cy} r={index % 5 === 0 ? 3 : 2.2} />; })}
          </g>
          <g className="fill-brand-mint" opacity="0.84">
            {dots.map(([u, v], index) => { const [cx, cy] = target(u, v); return <circle key={`target-dot-${index}`} cx={cx} cy={cy} r={index % 5 === 0 ? 3.2 : 2.35} />; })}
          </g>

          <g>
            <text x="48" y="536" className="fill-foreground-muted font-mono text-[15px] uppercase tracking-[0.075em]">FIND POSITIVE-INTEGER SOLUTIONS</text>
            <circle cx="56" cy="565" r="4.5" className="fill-brand-pink" />
            <text x="72" y="572" className="fill-brand-pink font-display text-[27px] italic">a² + b² = c²</text>
            <circle cx="56" cy="598" r="4.5" className="fill-brand-amber" />
            <text x="72" y="605" className="fill-brand-amber font-display text-[27px] italic">aⁿ + bⁿ = cⁿ</text>
          </g>

          {highlighted.map((seed, index) => {
            const [sourceX, sourceY] = source(...seed.source);
            const [targetX, targetY] = target(...seed.target);
            const controlPoints: XY[] = index === 0
              ? [[sourceX, sourceY], [355, 306], [416, 390], [492, 315], [525, 176], [633, 176], [742, 170], [targetX, targetY]]
              : [[sourceX, sourceY], [356, 334], [420, 248], [493, 304], [566, 472], [662, 440], [758, 420], [targetX, targetY]];
            const path = spline(controlPoints, 0.90);
            const labelX = targetX + 16;
            const labelY = targetY + (index === 0 ? -17 : 29);
            const accent = seed.kind === 'pink';
            return (
              <g key={seed.kind}>
                <path d={path} className={accent ? 'fill-none stroke-brand-pink' : 'fill-none stroke-brand-amber'} strokeWidth="3.15" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx={sourceX} cy={sourceY} r="5" className={accent ? 'fill-brand-pink' : 'fill-brand-amber'} />
                <circle cx={targetX} cy={targetY} r="5.5" className={accent ? 'fill-brand-pink' : 'fill-brand-amber'} />
                <text x={labelX} y={labelY} className={accent ? 'fill-brand-pink font-mono text-[17px] font-medium tracking-[0.05em]' : 'fill-brand-amber font-mono text-[17px] font-medium tracking-[0.05em]'} paintOrder="stroke" stroke="hsl(var(--background))" strokeWidth="2.5">{seed.label}</text>
              </g>
            );
          })}
        </svg>

        <svg viewBox="0 0 390 840" className="mx-auto block h-auto w-full max-w-[430px] md:hidden" role="img" aria-labelledby="weather-mobile-title weather-mobile-desc">
          <title id="weather-mobile-title">Two nearby prompts separate in model routing space</title>
          <desc id="weather-mobile-desc">Two nearly identical equations are close in text embedding space but far apart in difficulty. The faint paths suggest how sharply model rollouts can diverge after a small change in the prompt.</desc>
          <defs>
            <linearGradient id="weather-mobile-flow-fade" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="hsl(var(--foreground-muted))" stopOpacity="0.18" />
              <stop offset="0.48" stopColor="hsl(var(--foreground-secondary))" stopOpacity="0.36" />
              <stop offset="1" stopColor="hsl(var(--foreground-muted))" stopOpacity="0.16" />
            </linearGradient>
          </defs>

          <g className="fill-foreground-secondary font-mono text-[13px] uppercase tracking-[0.09em]">
            <text x="22" y="38" paintOrder="stroke" stroke="hsl(var(--background))" strokeWidth="5">TEXT EMBEDDING SPACE</text>
            <text x="368" y="816" textAnchor="end" paintOrder="stroke" stroke="hsl(var(--background))" strokeWidth="5">MODEL ROUTING SPACE</text>
          </g>

          <g>
            <text x="22" y="88" className="fill-foreground-muted font-mono text-[12px] uppercase tracking-[0.075em]">FIND POSITIVE-INTEGER SOLUTIONS</text>
            <circle cx="29" cy="116" r="4" className="fill-brand-pink" />
            <text x="44" y="123" className="fill-brand-pink font-display text-[21px] italic">a² + b² = c²</text>
            <circle cx="29" cy="145" r="4" className="fill-brand-amber" />
            <text x="44" y="152" className="fill-brand-amber font-display text-[21px] italic">aⁿ + bⁿ = cⁿ</text>
          </g>

          <g aria-hidden="true">
            <path d={mobileSourceSurfaceBoundary} fill="hsl(var(--brand-mint))" opacity="0.035" />
            <path d={mobileTargetSurfaceBoundary} fill="hsl(var(--brand-mint))" opacity="0.035" />
            {mobileSourceSurfaceCells.map(cell => (
              <polygon
                key={`mobile-source-surface-${cell.key}`}
                points={cell.points.map(([px, py]) => `${px.toFixed(1)},${py.toFixed(1)}`).join(' ')}
                fill="hsl(var(--brand-mint))"
                opacity={0.020 + cell.light * 0.090}
              />
            ))}
            {mobileTargetSurfaceCells.map(cell => (
              <polygon
                key={`mobile-target-surface-${cell.key}`}
                points={cell.points.map(([px, py]) => `${px.toFixed(1)},${py.toFixed(1)}`).join(' ')}
                fill="hsl(var(--brand-mint))"
                opacity={0.020 + cell.light * 0.090}
              />
            ))}
          </g>

          {threadSeeds.slice(0, 14).map(([u, v], index) => {
            const [sourceX, sourceY] = mobileSource(u, v);
            const [targetX, targetY] = mobileTarget(u, v);
            const phase = Math.sin((u * 13 + v * 17) * Math.PI);
            const bend = Math.cos((u * 19 - v * 11) * Math.PI);
            const path = spline([
              [sourceX, sourceY],
              [sourceX + 32 * bend, 315],
              [165 + 58 * phase, 375],
              [195 + 48 * bend, 438],
              [258 - 44 * phase, 505],
              [targetX + 22 * bend, 560],
              [targetX, targetY],
            ], 0.92);
            return <path key={`mobile-flow-${index}`} d={path} fill="none" stroke="url(#weather-mobile-flow-fade)" strokeWidth="1.05" strokeLinecap="round" opacity={0.46 + (index % 4) * 0.055} />;
          })}

          <g className="fill-none stroke-brand-mint" strokeWidth="1.05" strokeLinecap="round">
            {levels.map((level, index) => <path key={`mobile-source-v-${level}`} d={pathFrom(mobileSource, level, true, 30)} opacity={index === 0 || index === levels.length - 1 ? 0.72 : 0.43} />)}
            {levels.map((level, index) => <path key={`mobile-source-h-${level}`} d={pathFrom(mobileSource, level, false, 30)} opacity={index === 0 || index === levels.length - 1 ? 0.72 : 0.43} />)}
          </g>
          <g className="fill-none stroke-brand-mint" strokeWidth="1.08" strokeLinecap="round">
            {levels.map((level, index) => <path key={`mobile-target-v-${level}`} d={pathFrom(mobileTarget, level, true, 36)} opacity={index === 0 || index === levels.length - 1 ? 0.72 : 0.43} />)}
            {levels.map((level, index) => <path key={`mobile-target-h-${level}`} d={pathFrom(mobileTarget, level, false, 36)} opacity={index === 0 || index === levels.length - 1 ? 0.72 : 0.43} />)}
          </g>

          <g className="fill-brand-mint" opacity="0.84">
            {dots.map(([u, v], index) => { const [cx, cy] = mobileSource(u, v); return <circle key={`mobile-source-dot-${index}`} cx={cx} cy={cy} r={index % 5 === 0 ? 2.8 : 2} />; })}
          </g>
          <g className="fill-brand-mint" opacity="0.84">
            {dots.map(([u, v], index) => { const [cx, cy] = mobileTarget(u, v); return <circle key={`mobile-target-dot-${index}`} cx={cx} cy={cy} r={index % 5 === 0 ? 2.9 : 2.1} />; })}
          </g>

          {highlighted.map((seed, index) => {
            const [sourceX, sourceY] = mobileSource(...seed.source);
            const [targetX, targetY] = mobileTarget(...seed.target);
            const controlPoints: XY[] = index === 0
              ? [[sourceX, sourceY], [134, 294], [122, 356], [157, 414], [174, 465], [132, 520], [targetX, targetY]]
              : [[sourceX, sourceY], [258, 294], [270, 356], [233, 414], [212, 466], [276, 538], [targetX, targetY]];
            const path = spline(controlPoints, 0.90);
            const labelX = targetX + (index === 0 ? 11 : -11);
            const labelY = targetY + (index === 0 ? -14 : 23);
            const labelAnchor = index === 0 ? 'start' : 'end';
            const accent = seed.kind === 'pink';
            return (
              <g key={`mobile-${seed.kind}`}>
                <path d={path} className={accent ? 'fill-none stroke-brand-pink' : 'fill-none stroke-brand-amber'} strokeWidth="3.15" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx={sourceX} cy={sourceY} r="4.8" className={accent ? 'fill-brand-pink' : 'fill-brand-amber'} />
                <circle cx={targetX} cy={targetY} r="5.2" className={accent ? 'fill-brand-pink' : 'fill-brand-amber'} />
                <text x={labelX} y={labelY} textAnchor={labelAnchor} className={accent ? 'fill-brand-pink font-mono text-[13px] font-medium tracking-[0.05em]' : 'fill-brand-amber font-mono text-[13px] font-medium tracking-[0.05em]'} paintOrder="stroke" stroke="hsl(var(--background))" strokeWidth="2.25">{seed.label}</text>
              </g>
            );
          })}
        </svg>
    </RouterEmbedFigure>
  );
}

function LayerRoutabilityFigure() {
  const series: XY[] = [
    [0, 59.2], [7, 59.45], [15, 59.65], [24, 59.85], [34, 60.35], [44, 61.4],
    [54, 63.15], [61, 64.25], [68, 63.75], [76, 62.1], [86, 60.65], [100, 59.75],
  ];
  const left = 98;
  const right = 948;
  const top = 68;
  const bottom = 392;
  const x = (value: number) => left + (value / 100) * (right - left);
  const y = (value: number) => bottom - ((value - 58.5) / 6.5) * (bottom - top);
  const points = series.map(([depth, auc]) => [x(depth), y(auc)] as XY);
  const curve = spline(points, 0.9);
  const peak = series.reduce((best, point) => point[1] > best[1] ? point : best, series[0]);

  return (
    <RouterEmbedFigure name="layer-routability">
        <svg viewBox="0 0 1020 520" className="block h-auto w-full max-w-full" role="img" aria-labelledby="layer-title layer-desc">
          <title id="layer-title">Routability peaks in the middle layers of MiniCPM5-1B</title>
          <desc id="layer-desc">Hidden states from different depths of MiniCPM5-1B are compared by how well they distinguish the best model family. The routing signal peaks just after the network midpoint and falls toward the output.</desc>
          <defs>
            <linearGradient id="layer-signal-stroke" x1={left} y1="0" x2={right} y2="0" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#5E626E" />
              <stop offset="0.38" stopColor="#B66AAA" />
              <stop offset="0.55" stopColor="#E88BD5" />
              <stop offset="0.61" stopColor="#F5A623" />
              <stop offset="0.69" stopColor="#E88BD5" />
              <stop offset="1" stopColor="#5E626E" />
            </linearGradient>
          </defs>
          {[59, 61, 63, 65].map(tick => (
            <g key={tick}>
              <line x1={left} x2={right} y1={y(tick)} y2={y(tick)} className="stroke-border" strokeWidth="1" />
              <text x={left - 16} y={y(tick) + 6} textAnchor="end" className="fill-foreground-secondary font-mono text-[24px] sm:text-[19px]">{tick}%</text>
            </g>
          ))}
          {[0, 25, 50, 75, 100].map(tick => (
            <g key={tick}>
              <line x1={x(tick)} x2={x(tick)} y1={bottom + 8} y2={bottom + 15} className="stroke-foreground-muted" strokeWidth="1" />
              <text x={x(tick)} y={bottom + 52} textAnchor="middle" className="fill-foreground-secondary font-mono text-[24px] sm:text-[19px]">{tick}%</text>
            </g>
          ))}
          <text x={left} y="41" className="fill-foreground-secondary font-mono text-[22px] uppercase tracking-[0.08em] sm:text-[18px]">MiniCPM5-1B</text>
          <text x={(left + right) / 2} y="496" textAnchor="middle" className="fill-foreground-secondary font-mono text-[22px] uppercase tracking-[0.08em] sm:text-[18px]">Layer depth</text>
          <text x="25" y={(top + bottom) / 2} transform={`rotate(-90 25 ${(top + bottom) / 2})`} textAnchor="middle" className="fill-foreground-secondary font-mono text-[20px] uppercase tracking-[0.06em] sm:text-[17px]">Oracle model-family AUC</text>
          <path d={curve} className="fill-none stroke-background" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
          <path d={curve} fill="none" stroke="url(#layer-signal-stroke)" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />

          {series.map(([depth, auc]) => {
            const isPeak = depth === peak[0];
            const inSignalBand = depth >= 44 && depth <= 76;
            return (
              <g key={depth}>
                <line
                  x1={x(depth)}
                  x2={x(depth)}
                  y1={bottom + 4}
                  y2={bottom + (isPeak ? 18 : 13)}
                  className={isPeak ? 'stroke-brand-amber' : inSignalBand ? 'stroke-brand-pink' : 'stroke-foreground-muted'}
                  strokeWidth={isPeak ? 2 : 1.2}
                  opacity={isPeak ? 1 : inSignalBand ? 0.72 : 0.46}
                  strokeLinecap="round"
                />
                <circle
                  cx={x(depth)}
                  cy={y(auc)}
                  r={isPeak ? 6.5 : 3.4}
                  className={isPeak ? 'fill-background stroke-brand-amber' : inSignalBand ? 'fill-background stroke-brand-pink' : 'fill-background stroke-foreground-muted'}
                  strokeWidth={isPeak ? 3 : 1.35}
                />
              </g>
            );
          })}

          <path d={`M ${x(peak[0]) + 9} ${y(peak[1]) - 7} C ${x(peak[0]) + 31} ${y(peak[1]) - 31}, ${x(peak[0]) + 54} ${y(peak[1]) - 35}, ${x(peak[0]) + 79} ${y(peak[1]) - 35}`} className="fill-none stroke-border" />
          <text x={x(peak[0]) + 89} y={y(peak[1]) - 29} className="fill-brand-amber font-mono text-[24px] font-medium uppercase tracking-[0.04em] sm:text-[19px]">Peak routing signal</text>
        </svg>
    </RouterEmbedFigure>
  );
}

type FingerprintPoint = {
  id: string;
  label: string;
  lab: 'OpenAI' | 'Google' | 'DeepSeek' | 'Qwen' | 'Zhipu';
  x: number;
  y: number;
  z: number;
};

type FingerprintWorldPoint = {
  x: number;
  y: number;
  z: number;
};

const FINGERPRINTS: FingerprintPoint[] = [
  { id: 'gemini-flash-lite', label: 'Gemini 3.1 Flash-Lite', lab: 'Google', x: -3.25461, y: -0.89658, z: -1.07989 },
  { id: 'gpt-oss-20b', label: 'gpt-oss 20B', lab: 'OpenAI', x: -6.65269, y: 1.35216, z: 0.70206 },
  { id: 'gpt-oss-120b', label: 'gpt-oss 120B', lab: 'OpenAI', x: -3.798, y: 0.97897, z: -0.35958 },
  { id: 'gpt-54-nano', label: 'GPT-5.4 Nano', lab: 'OpenAI', x: -0.45309, y: 0.59067, z: 0.17667 },
  { id: 'deepseek-v4-flash', label: 'DeepSeek V4 Flash', lab: 'DeepSeek', x: 0.23965, y: 0.6743, z: -0.36855 },
  { id: 'gpt-54-mini', label: 'GPT-5.4 Mini', lab: 'OpenAI', x: 0.69296, y: 0.38854, z: -0.22048 },
  { id: 'deepseek-v4-pro', label: 'DeepSeek V4 Pro', lab: 'DeepSeek', x: 1.48339, y: 0.25387, z: 0.22783 },
  { id: 'gemini-pro', label: 'Gemini 3.1 Pro', lab: 'Google', x: 2.73445, y: -1.11048, z: -0.62085 },
  { id: 'gpt-54', label: 'GPT-5.4', lab: 'OpenAI', x: 3.47615, y: 0.05942, z: 0.88741 },
  { id: 'glm-52', label: 'GLM-5.2', lab: 'Zhipu', x: 2.82078, y: 0.26014, z: 0.63597 },
  { id: 'qwen3-235b', label: 'Qwen3 235B', lab: 'Qwen', x: -5.90573, y: -0.90691, z: 0.75207 },
  { id: 'qwen3-coder', label: 'Qwen3 Coder', lab: 'Qwen', x: -5.73217, y: -0.04279, z: 0.51765 },
  { id: 'qwen3-next', label: 'Qwen3 Next 80B', lab: 'Qwen', x: -6.82938, y: -1.04915, z: 0.44615 },
];

const LAB_COLORS: Record<FingerprintPoint['lab'], string> = {
  OpenAI: '#A5A7AE',
  Google: '#E88BD5',
  DeepSeek: '#F5A623',
  Qwen: '#4ADE80',
  Zhipu: '#8FA8FF',
};

function ModelFingerprintFigure() {
  const [yaw, setYaw] = useState(-0.58);
  const [pitch, setPitch] = useState(-0.28);
  const [active, setActive] = useState<string | null>(null);
  const drag = useRef<{ x: number; y: number } | null>(null);
  const bounds = useMemo(() => ({
    x: [Math.min(...FINGERPRINTS.map(point => point.x)), Math.max(...FINGERPRINTS.map(point => point.x))],
    y: [Math.min(...FINGERPRINTS.map(point => point.y)), Math.max(...FINGERPRINTS.map(point => point.y))],
    z: [Math.min(...FINGERPRINTS.map(point => point.z)), Math.max(...FINGERPRINTS.map(point => point.z))],
  }), []);
  const normalize = (value: number, range: number[]) => ((value - range[0]) / (range[1] - range[0])) * 2 - 1;
  const worldBounds = {
    x: [-1.75, 1.75],
    y: [-1.04, 1.04],
    z: [-1.18, 1.18],
  };
  const toWorld = (point: FingerprintPoint): FingerprintWorldPoint => ({
    x: normalize(point.x, bounds.x) * worldBounds.x[1],
    y: normalize(point.z, bounds.z) * worldBounds.y[1],
    z: normalize(point.y, bounds.y) * worldBounds.z[1],
  });
  const projectWorld = ({ x: wx, y: wy, z: wz }: FingerprintWorldPoint) => {
    const cy = Math.cos(yaw);
    const sy = Math.sin(yaw);
    const x1 = wx * cy + wz * sy;
    const z1 = -wx * sy + wz * cy;
    const cp = Math.cos(pitch);
    const sp = Math.sin(pitch);
    const y1 = wy * cp - z1 * sp;
    const z2 = wy * sp + z1 * cp;
    const perspective = 4.8 / (4.8 + z2);
    return { x: 540 + x1 * 175 * perspective, y: 280 - y1 * 165 * perspective, z: z2, perspective };
  };
  const project = (point: FingerprintPoint) => projectWorld(toWorld(point));
  const projected = FINGERPRINTS.map(point => ({ point, world: toWorld(point), screen: project(point) })).sort((a, b) => b.screen.z - a.screen.z);
  const axisOriginWorld = { x: worldBounds.x[0], y: worldBounds.y[0], z: worldBounds.z[0] };
  const axisOrigin = projectWorld(axisOriginWorld);
  const axes = [
    { id: 'pc1', label: 'PC1 · 84.7%', end: projectWorld({ ...axisOriginWorld, x: worldBounds.x[1] }), outward: 18, perpendicular: 10 },
    { id: 'pc2', label: 'PC2 · 5.6%', end: projectWorld({ ...axisOriginWorld, z: worldBounds.z[1] }), outward: 18, perpendicular: 22 },
    { id: 'pc3', label: 'PC3 · 4.0%', end: projectWorld({ ...axisOriginWorld, y: worldBounds.y[1] }), outward: 18, perpendicular: -12 },
  ];
  const axisLabelPosition = (end: { x: number; y: number }, outward: number, perpendicular: number) => {
    const dx = end.x - axisOrigin.x;
    const dy = end.y - axisOrigin.y;
    const length = Math.max(1, Math.hypot(dx, dy));
    const ux = dx / length;
    const uy = dy / length;
    return {
      x: Math.max(55, Math.min(1025, end.x + ux * outward - uy * perpendicular)),
      y: Math.max(78, Math.min(550, end.y + uy * outward + ux * perpendicular)),
      anchor: ux < -0.16 ? 'end' as const : ux > 0.16 ? 'start' as const : 'middle' as const,
    };
  };
  const xGrid = [-1.75, -0.875, 0, 0.875, 1.75].map(value => ({
    id: `x-${value}`,
    start: projectWorld({ x: value, y: worldBounds.y[0], z: worldBounds.z[0] }),
    end: projectWorld({ x: value, y: worldBounds.y[0], z: worldBounds.z[1] }),
  }));
  const zGrid = [-1.18, -0.393, 0.393, 1.18].map(value => ({
    id: `z-${value}`,
    start: projectWorld({ x: worldBounds.x[0], y: worldBounds.y[0], z: value }),
    end: projectWorld({ x: worldBounds.x[1], y: worldBounds.y[0], z: value }),
  }));

  const onPointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!drag.current) return;
    const dx = event.clientX - drag.current.x;
    const dy = event.clientY - drag.current.y;
    drag.current = { x: event.clientX, y: event.clientY };
    setYaw(value => value + dx * 0.008);
    setPitch(value => Math.max(-0.85, Math.min(0.45, value + dy * 0.006)));
  };

  return (
    <RouterEmbedFigure name="model-fingerprints">
        <svg
          viewBox="0 0 1080 700"
          className="block h-auto w-full max-w-full cursor-grab select-none touch-pan-y active:cursor-grabbing"
          role="img"
          aria-labelledby="fingerprint-title fingerprint-desc"
          onPointerDown={event => {
            event.preventDefault();
            event.currentTarget.setPointerCapture(event.pointerId);
            drag.current = { x: event.clientX, y: event.clientY };
          }}
          onPointerMove={onPointerMove}
          onPointerUp={() => { drag.current = null; }}
          onPointerCancel={() => { drag.current = null; }}
        >
          <title id="fingerprint-title">Three-dimensional projection of model capability fingerprints</title>
          <desc id="fingerprint-desc">Model capability fingerprints projected into three dimensions. Nearby models have similar combinations of benchmark performance, latency, and price. Drag to rotate.</desc>
          <text x="42" y="46" className="fill-foreground-secondary font-mono text-[30px] uppercase tracking-[0.06em] sm:text-[18px]">Drag to rotate</text>
          {[...xGrid, ...zGrid].map(line => (
            <line key={line.id} x1={line.start.x} y1={line.start.y} x2={line.end.x} y2={line.end.y} className="stroke-border" strokeWidth="1" opacity="0.65" />
          ))}
          {axes.map(axis => {
            const label = axisLabelPosition(axis.end, axis.outward, axis.perpendicular);
            return (
              <g key={axis.id}>
                <line x1={axisOrigin.x} y1={axisOrigin.y} x2={axis.end.x} y2={axis.end.y} className="stroke-foreground-muted" strokeWidth="1.2" />
                <text x={label.x} y={label.y} textAnchor={label.anchor} dominantBaseline="middle" className="fill-foreground-secondary font-mono text-[24px] uppercase sm:hidden">{axis.id.toUpperCase()}</text>
                <text x={label.x} y={label.y} textAnchor={label.anchor} dominantBaseline="middle" className="hidden fill-foreground-secondary font-mono text-[18px] uppercase sm:block">{axis.label}</text>
              </g>
            );
          })}

          {(['OpenAI', 'Google', 'DeepSeek', 'Qwen', 'Zhipu'] as const).map((lab, index) => (
            <g key={lab} transform={`translate(${382 + index * 132} 40)`}>
              <circle cx="0" cy="0" r="4" fill={LAB_COLORS[lab]} />
              <text x="11" y="6" className="fill-foreground-secondary font-mono text-[20px] uppercase tracking-[0.02em] sm:hidden">{lab === 'DeepSeek' ? 'DS' : lab === 'Zhipu' ? 'GLM' : lab}</text>
              <text x="11" y="6" className="hidden fill-foreground-secondary font-mono text-[16px] uppercase tracking-[0.04em] sm:block">{lab}</text>
            </g>
          ))}

          {projected.map(({ point, world, screen }) => {
            const isActive = active === point.id;
            const labelRight = screen.x < 690;
            const radius = (isActive ? 7 : 5) * Math.max(0.82, Math.min(1.18, screen.perspective));
            const floor = projectWorld({ ...world, y: worldBounds.y[0] });
            return (
              <g key={point.id} onPointerEnter={() => setActive(point.id)} onPointerLeave={() => setActive(null)}>
                <line x1={screen.x} y1={screen.y} x2={floor.x} y2={floor.y} stroke={LAB_COLORS[point.lab]} opacity="0.2" strokeWidth="1" />
                {isActive && <circle cx={screen.x} cy={screen.y} r={radius + 9} fill={LAB_COLORS[point.lab]} opacity="0.12" />}
                <circle cx={screen.x} cy={screen.y} r={radius} fill={LAB_COLORS[point.lab]} stroke="hsl(var(--background))" strokeWidth="2.5" />
                <circle cx={screen.x} cy={screen.y} r="18" fill="transparent" />
                {(isActive || ['gpt-54', 'gpt-54-mini', 'gemini-pro', 'qwen3-next', 'deepseek-v4-pro', 'glm-52'].includes(point.id)) && (
                  <>
                    <path d={`M ${screen.x} ${screen.y} Q ${screen.x + (labelRight ? 16 : -16)} ${screen.y - 8}, ${screen.x + (labelRight ? 25 : -25)} ${screen.y - 18}`} className="fill-none stroke-border" strokeWidth="1" />
                    <text x={screen.x + (labelRight ? 31 : -31)} y={screen.y - 17} textAnchor={labelRight ? 'start' : 'end'} className={isActive ? 'fill-foreground font-mono text-[24px] sm:text-[18px]' : 'hidden fill-foreground font-mono text-[17px] min-[480px]:block'}>{point.label}</text>
                  </>
                )}
              </g>
            );
          })}
        </svg>
    </RouterEmbedFigure>
  );
}

function SyntheticAugmentationFigure() {
  const anchorDots = [
    [82, 245], [104, 286], [119, 211], [137, 320], [151, 260], [169, 225],
    [184, 294], [202, 247], [218, 329], [229, 202], [96, 341], [197, 350],
  ];
  const descendants = [
    { x: 422, y: 184 },
    { x: 436, y: 232 },
    { x: 428, y: 280 },
    { x: 410, y: 328 },
  ];
  const matrix = [
    [0.20, 0.42, 0.70],
    [0.34, 0.82, 0.46],
    [0.66, 0.30, 0.92],
  ];

  return (
    <RouterEmbedFigure name="synthetic-loop">
        <svg viewBox="0 0 390 930" className="mx-auto block h-auto w-full max-w-[430px] md:hidden" role="img" aria-labelledby="synthetic-mobile-title synthetic-mobile-desc">
          <title id="synthetic-mobile-title">Synthetic task generation and validation loop</title>
          <desc id="synthetic-mobile-desc">Language-model workflows use web search, code, and other tools to generate and validate new tasks around requests where choosing the wrong model and budget is costly or evidence is sparse. Accepted tasks preserve a useful pattern of model successes and failures.</desc>
          <defs>
            <marker id="foundry-mobile-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" className="fill-foreground-muted" />
            </marker>
            <marker id="foundry-mobile-arrow-pink" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" className="fill-brand-pink" />
            </marker>
            <marker id="foundry-mobile-arrow-mint" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" className="fill-brand-mint" />
            </marker>
          </defs>

          <g>
            <text x="24" y="34" className="fill-foreground-secondary font-mono text-[14px] uppercase tracking-[0.08em]">01</text>
            <text x="54" y="34" className="fill-foreground font-display text-[18px]">Select anchors</text>
            <line x1="24" x2="342" y1="50" y2="50" className="stroke-border" />
            <path d="M 52 126 C 94 72, 146 70, 183 102 M 68 158 C 125 106, 190 106, 232 139 M 126 177 C 189 137, 257 143, 308 169" className="fill-none stroke-border" />
            {[[62, 124], [86, 91], [114, 145], [151, 95], [180, 151], [214, 119], [248, 166], [286, 138], [313, 175]].map(([cx, cy], index) => (
              <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={index % 3 === 0 ? 5 : 3.5} className="fill-foreground-muted" opacity={0.45 + (index % 3) * 0.15} />
            ))}
            <circle cx="86" cy="91" r="10" className="fill-none stroke-brand-amber" />
            <circle cx="180" cy="151" r="11" className="fill-none stroke-brand-amber" />
            <circle cx="286" cy="138" r="10" className="fill-none stroke-brand-amber" />
            <text x="42" y="76" className="fill-foreground-secondary font-mono text-[12px] uppercase">Regret</text>
            <text x="147" y="181" className="fill-foreground-secondary font-mono text-[12px] uppercase">Uncertainty</text>
            <text x="273" y="116" className="fill-foreground-secondary font-mono text-[12px] uppercase">Disagreement</text>
          </g>
          <path d="M 195 194 V 218" className="fill-none stroke-foreground-muted" markerEnd="url(#foundry-mobile-arrow)" />

          <g>
            <text x="24" y="246" className="fill-foreground-secondary font-mono text-[14px] uppercase tracking-[0.08em]">02</text>
            <text x="54" y="246" className="fill-foreground font-display text-[18px]">Generate</text>
            <line x1="24" x2="342" y1="262" y2="262" className="stroke-border" />
            <circle cx="76" cy="304" r="7" className="fill-foreground-secondary" opacity="0.72" />
            <circle cx="150" cy="322" r="7" className="fill-foreground-secondary" opacity="0.72" />
            <circle cx="224" cy="296" r="7" className="fill-foreground-secondary" opacity="0.72" />
            <circle cx="298" cy="318" r="7" className="fill-foreground-secondary" opacity="0.72" />
            <path d="M 83 304 C 104 304, 119 320, 143 322 M 157 322 C 180 322, 193 296, 217 296 M 231 296 C 255 296, 269 318, 291 318" className="fill-none stroke-border" />
            {['WEB', 'CODE', 'TOOLS'].map((label, index) => (
              <g key={`mobile-tool-${label}`} transform={`translate(${80 + index * 84} 348)`}>
                <rect width="70" height="28" rx="14" className="fill-foreground/[0.03] stroke-border" />
                <text x="35" y="19" textAnchor="middle" className="fill-foreground-secondary font-mono text-[12px]">{label}</text>
              </g>
            ))}
          </g>
          <path d="M 195 392 V 416" className="fill-none stroke-foreground-muted" markerEnd="url(#foundry-mobile-arrow)" />

          <g>
            <text x="24" y="444" className="fill-foreground-secondary font-mono text-[14px] uppercase tracking-[0.08em]">03</text>
            <text x="54" y="444" className="fill-foreground font-display text-[18px]">Validate</text>
            <line x1="24" x2="342" y1="460" y2="460" className="stroke-border" />
            {[
              { x: 78, label: 'Task fidelity' },
              { x: 195, label: 'Gold answer' },
              { x: 312, label: 'Anti-replay' },
            ].map(gate => (
              <g key={`mobile-gate-${gate.label}`}>
                <text x={gate.x} y="490" textAnchor="middle" className="fill-foreground-secondary font-mono text-[12px] uppercase">{gate.label}</text>
                <circle cx={gate.x} cy="520" r="13" className="fill-background stroke-foreground-muted" strokeWidth="1.5" />
                <circle cx={gate.x} cy="520" r="4" className="fill-brand-mint" />
              </g>
            ))}
            <path d="M 92 520 H 181 M 209 520 H 298" className="fill-none stroke-foreground-muted" markerEnd="url(#foundry-mobile-arrow)" />
            <line x1="188" x2="202" y1="513" y2="527" className="stroke-brand-pink" strokeWidth="2" strokeLinecap="round" />
            <line x1="202" x2="188" y1="513" y2="527" className="stroke-brand-pink" strokeWidth="2" strokeLinecap="round" />
            <path d="M 195 534 C 220 552, 218 576, 196 584" className="fill-none stroke-brand-pink" strokeWidth="1.4" markerEnd="url(#foundry-mobile-arrow-pink)" />
            <rect x="143" y="578" width="104" height="34" rx="17" className="fill-brand-pink/[0.07] stroke-brand-pink/60" />
            <text x="195" y="600" textAnchor="middle" className="fill-foreground font-mono text-[13px] uppercase tracking-[0.07em]">Repair</text>
            <path d="M 143 595 C 106 595, 82 566, 82 536" className="fill-none stroke-brand-pink" strokeWidth="1.4" markerEnd="url(#foundry-mobile-arrow-pink)" />
          </g>
          <path d="M 195 626 V 650" className="fill-none stroke-foreground-muted" markerEnd="url(#foundry-mobile-arrow)" />

          <g>
            <text x="24" y="678" className="fill-foreground-secondary font-mono text-[14px] uppercase tracking-[0.08em]">04</text>
            <text x="54" y="678" className="fill-foreground font-display text-[18px]">Score actions</text>
            <line x1="24" x2="342" y1="694" y2="694" className="stroke-border" />
            <text x="195" y="720" textAnchor="middle" className="fill-foreground-secondary font-mono text-[12px] uppercase tracking-[0.06em]">model × budget</text>
            {matrix.map((row, rowIndex) => row.map((value, columnIndex) => (
              <rect
                key={`mobile-matrix-${rowIndex}-${columnIndex}`}
                x={139 + columnIndex * 42}
                y={736 + rowIndex * 34}
                width="30"
                height="24"
                rx="4"
                fill="#F5A623"
                opacity={0.16 + value * 0.74}
              />
            )))}
          </g>
          <path d="M 195 838 V 856" className="fill-none stroke-brand-mint" markerEnd="url(#foundry-mobile-arrow-mint)" />

          <g>
            <text x="24" y="884" className="fill-foreground-secondary font-mono text-[14px] uppercase tracking-[0.08em]">05</text>
            <text x="54" y="884" className="fill-foreground font-display text-[18px]">Keep task</text>
            <circle cx="287" cy="879" r="22" className="fill-brand-mint/[0.08] stroke-brand-mint/60" />
            <path d="M 277 879 L 284 886 L 298 870" className="fill-none stroke-brand-mint" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </g>
          <path d="M 310 903 C 370 916, 372 170, 330 148" className="fill-none stroke-foreground-muted" strokeWidth="1.2" strokeDasharray="4 7" markerEnd="url(#foundry-mobile-arrow)" />
          <text x="360" y="554" transform="rotate(-90 360 554)" textAnchor="middle" className="fill-foreground-secondary font-mono text-[11px] uppercase tracking-[0.07em]">accepted tasks guide the next round</text>
        </svg>

        <svg viewBox="0 0 1140 600" className="hidden h-auto w-full max-w-full md:block" role="img" aria-labelledby="synthetic-title synthetic-desc">
          <title id="synthetic-title">Synthetic task generation and validation loop</title>
          <desc id="synthetic-desc">Language-model workflows use web search, code, and other tools to generate new tasks around requests where choosing the wrong model and budget is costly or evidence is sparse. Candidates must preserve the anchor task’s required skills and difficulty, have a verified reference answer, pass anti-replay checks, and reproduce a useful pattern of model successes and failures.</desc>
          <defs>
            <marker id="foundry-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" className="fill-foreground-muted" />
            </marker>
            <marker id="foundry-arrow-pink" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" className="fill-brand-pink" />
            </marker>
            <marker id="foundry-arrow-mint" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" className="fill-brand-mint" />
            </marker>
          </defs>

          {[266, 474, 748, 970].map(x => (
            <line key={x} x1={x} x2={x} y1="58" y2="466" className="stroke-border" strokeWidth="1" strokeDasharray="2 8" opacity="0.65" />
          ))}

          <g>
            <text x="52" y="72" className="fill-foreground-secondary font-mono text-[20px] uppercase tracking-[0.08em] sm:text-[18px]">01</text>
            <text x="52" y="108" className="fill-foreground font-display text-[31px] sm:text-[29px]">Select anchors</text>
            <path d="M 66 332 C 92 188, 170 164, 239 211 M 66 301 C 112 218, 184 207, 239 249 M 72 357 C 126 283, 194 282, 238 318" className="fill-none stroke-border" strokeWidth="1.2" opacity="0.75" />
            {anchorDots.map(([cx, cy], index) => (
              <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={index % 4 === 0 ? 4.5 : 3.2} className="fill-foreground-muted" opacity={0.38 + (index % 3) * 0.16} />
            ))}
            <circle cx="119" cy="211" r="19" className="fill-none stroke-brand-amber" strokeWidth="1.6" opacity="0.45" />
            <circle cx="119" cy="211" r="6" className="fill-brand-amber" />
            <circle cx="184" cy="294" r="24" className="fill-none stroke-brand-amber" strokeWidth="1.6" opacity="0.55" />
            <circle cx="184" cy="294" r="6" className="fill-brand-amber" />
            <circle cx="96" cy="341" r="16" className="fill-none stroke-brand-amber" strokeWidth="1.6" opacity="0.38" />
            <circle cx="96" cy="341" r="5" className="fill-brand-amber" />
            <text x="88" y="181" className="fill-foreground-secondary font-mono text-[18px] uppercase sm:text-[17px]">High regret</text>
            <text x="171" y="269" className="fill-foreground-secondary font-mono text-[18px] uppercase sm:text-[17px]">Uncertainty</text>
            <text x="62" y="377" className="fill-foreground-secondary font-mono text-[18px] uppercase sm:text-[17px]">Disagreement</text>
          </g>

          <g>
            <text x="292" y="72" className="fill-foreground-secondary font-mono text-[20px] uppercase tracking-[0.08em] sm:text-[18px]">02</text>
            <text x="292" y="108" className="fill-foreground font-display text-[31px] sm:text-[29px]">Generate</text>
            <path d="M 220 294 C 258 294, 278 256, 310 256" className="fill-none stroke-foreground-muted" strokeWidth="1.4" markerEnd="url(#foundry-arrow)" />
            <circle cx="316" cy="256" r="8" className="fill-background stroke-brand-pink" strokeWidth="2.5" />
            {descendants.map((node, index) => (
              <g key={`${node.x}-${node.y}`}>
                <path d={`M 324 256 C 356 256, 369 ${node.y}, ${node.x - 9} ${node.y}`} className="fill-none stroke-foreground-muted" strokeWidth="1.15" opacity="0.85" />
                <circle cx={node.x} cy={node.y} r={index === 1 ? 7 : 5.5} className="fill-foreground-secondary" opacity={index === 1 ? 0.85 : 0.62} />
              </g>
            ))}
            <text x="292" y="389" className="fill-foreground-secondary font-mono text-[18px] uppercase tracking-[0.03em] sm:text-[17px]">New tasks</text>
            {['WEB', 'CODE', 'TOOLS'].map((label, index) => (
              <g key={label} transform={`translate(${292 + index * 57} 414)`}>
                <rect width="50" height="26" rx="13" className="fill-foreground/[0.03] stroke-border" />
                <text x="25" y="18" textAnchor="middle" className="fill-foreground-secondary font-mono text-[15px] sm:text-[15px]">{label}</text>
              </g>
            ))}
          </g>

          <g>
            <text x="500" y="72" className="fill-foreground-secondary font-mono text-[20px] uppercase tracking-[0.08em] sm:text-[18px]">03</text>
            <text x="500" y="108" className="fill-foreground font-display text-[31px] sm:text-[29px]">Validate</text>
            {[
              { x: 492, label: 'TASK FIDELITY' },
              { x: 607, label: 'GOLD ANSWER' },
              { x: 722, label: 'ANTI-REPLAY' },
            ].map(gate => (
              <g key={gate.label}>
                <text x={gate.x} y="151" textAnchor="middle" className="fill-foreground-secondary font-mono text-[14px] uppercase sm:text-[14px]">{gate.label}</text>
                <line x1={gate.x} x2={gate.x} y1="169" y2="333" className="stroke-border" strokeWidth="5" strokeLinecap="round" />
                {[203, 256, 309].map(y => <circle key={y} cx={gate.x} cy={y} r="9" className="fill-background stroke-foreground-muted" strokeWidth="1.5" />)}
              </g>
            ))}
            {[203, 256, 309].map((y, index) => (
              <path key={y} d={`M 444 ${184 + index * 48} C 470 ${184 + index * 48}, 480 ${y}, 513 ${y} L 721 ${y}`} className="fill-none stroke-foreground-muted" strokeWidth="1.35" markerEnd={index === 1 ? undefined : 'url(#foundry-arrow)'} />
            ))}
            {[492, 607, 722].flatMap(x => [203, 256, 309].map(y => <circle key={`${x}-${y}-pass`} cx={x} cy={y} r="3.2" className="fill-brand-mint" />))}
            <line x1="598" x2="616" y1="247" y2="265" className="stroke-brand-pink" strokeWidth="2.2" strokeLinecap="round" />
            <line x1="616" x2="598" y1="247" y2="265" className="stroke-brand-pink" strokeWidth="2.2" strokeLinecap="round" />
            <path d="M 607 268 C 638 300, 655 343, 626 382" className="fill-none stroke-brand-pink" strokeWidth="1.5" markerEnd="url(#foundry-arrow-pink)" />
            <rect x="554" y="385" width="122" height="42" rx="21" className="fill-brand-pink/[0.07] stroke-brand-pink/60" />
            <text x="615" y="412" textAnchor="middle" className="fill-foreground font-mono text-[19px] uppercase tracking-[0.08em] sm:text-[18px]">Repair</text>
            <path d="M 554 406 C 499 406, 485 355, 514 314" className="fill-none stroke-brand-pink" strokeWidth="1.5" markerEnd="url(#foundry-arrow-pink)" />
          </g>

          <g>
            <text x="772" y="72" className="fill-foreground-secondary font-mono text-[20px] uppercase tracking-[0.08em] sm:text-[18px]">04</text>
            <text x="772" y="108" className="fill-foreground font-display text-[31px] sm:text-[29px]">Score actions</text>
            <path d="M 720 203 C 744 203, 748 226, 771 226" className="fill-none stroke-foreground-muted" strokeWidth="1.4" markerEnd="url(#foundry-arrow)" />
            <text x="817" y="151" className="fill-foreground-secondary font-mono text-[18px] uppercase sm:text-[17px]">Budget</text>
            <text x="752" y="259" transform="rotate(-90 752 259)" textAnchor="middle" className="fill-foreground-secondary font-mono text-[18px] uppercase sm:text-[17px]">Model</text>
            {['b₁', 'b₂', 'b₃'].map((label, index) => (
              <text key={label} x={818 + index * 48} y="177" textAnchor="middle" className="fill-foreground-secondary font-mono text-[17px] sm:text-[16px]">{label}</text>
            ))}
            {matrix.map((row, rowIndex) => (
              <g key={rowIndex}>
                <text x="792" y={211 + rowIndex * 48} textAnchor="end" className="fill-foreground-secondary font-mono text-[17px] sm:text-[16px]">m{String.fromCharCode(65 + rowIndex)}</text>
                {row.map((value, columnIndex) => (
                  <rect
                    key={`${rowIndex}-${columnIndex}`}
                    x={800 + columnIndex * 48}
                    y={188 + rowIndex * 48}
                    width="36"
                    height="36"
                    rx="5"
                    fill="#F5A623"
                    opacity={0.16 + value * 0.74}
                  />
                ))}
              </g>
            ))}
            <text x="800" y="356" className="fill-foreground-secondary font-mono text-[18px] uppercase sm:text-[17px]">Compare choices</text>
          </g>

          <g>
            <text x="995" y="72" className="fill-foreground-secondary font-mono text-[20px] uppercase tracking-[0.08em] sm:text-[18px]">05</text>
            <text x="995" y="108" className="fill-foreground font-display text-[31px] sm:text-[29px]">Keep</text>
            <path d="M 944 238 C 968 238, 976 238, 995 238" className="fill-none stroke-brand-mint" strokeWidth="1.5" markerEnd="url(#foundry-arrow-mint)" />
            <circle cx="1036" cy="238" r="34" className="fill-brand-mint/[0.08] stroke-brand-mint/60" strokeWidth="1.5" />
            <path d="M 1020 239 L 1031 250 L 1052 224" className="fill-none stroke-brand-mint" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
            <text x="1036" y="305" textAnchor="middle" className="fill-foreground-secondary font-mono text-[18px] uppercase sm:text-[17px]">Failure pattern</text>
            <text x="1036" y="329" textAnchor="middle" className="fill-foreground-secondary font-mono text-[18px] uppercase sm:text-[17px]">preserved</text>
            <line x1="1026" x2="1046" y1="374" y2="394" className="stroke-brand-pink" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="1046" x2="1026" y1="374" y2="394" className="stroke-brand-pink" strokeWidth="2.5" strokeLinecap="round" />
            <text x="1036" y="425" textAnchor="middle" className="fill-foreground-muted font-mono text-[17px] uppercase sm:text-[16px]">Repair or reject</text>
          </g>

          <path d="M 890 366 C 958 512, 234 548, 151 403" className="fill-none stroke-foreground-muted" strokeWidth="1.3" strokeDasharray="4 8" markerEnd="url(#foundry-arrow)" />
          <text x="555" y="527" textAnchor="middle" className="fill-foreground-secondary font-mono text-[18px] uppercase tracking-[0.08em] sm:text-[17px]">Accepted tasks guide the next round</text>
        </svg>
    </RouterEmbedFigure>
  );
}

export function BlogEmbed({ name }: { name: BlogEmbedId }) {
  if (isKetteiEmbed(name)) return <KetteiFigure name={name} />;
  switch (name) {
    case 'router-frontier':
      return <RouterFrontierFigure />;
    case 'weather-map':
      return <WeatherMapFigure />;
    case 'routing-question-contrast':
      return <RoutingQuestionContrastEmbed />;
    case 'layer-routability':
      return <LayerRoutabilityFigure />;
    case 'model-fingerprints':
      return <ModelFingerprintFigure />;
    case 'router-architecture':
      return <RouterArchitectureFigure />;
    case 'router-playground':
      return <RouterPlaygroundEmbed />;
    case 'synthetic-loop':
      return <SyntheticAugmentationFigure />;
    default:
      return null;
  }
}
